/**
 * ============================================================================
 * Cortex sidebar configuration — the single source of truth for the shell rail
 * ============================================================================
 *
 * This file owns the CoordioCortex navigation tree. Every entry the shell rail
 * renders comes from {@link cortexSidebarItems}; nothing else may add, reorder
 * or relabel a nav node. The tree is a recursive `ShellSidebarItem` structure
 * consumed by `AppSidebar` / `AppShell` from `@coordio/ui`, and it mirrors the
 * CoordioCore model in `frontend/apps/core/src/shell/sidebar-config.tsx`.
 *
 * Conventions — copied verbatim from CoordioCore, keep them:
 *
 * 1. `key` is kebab-case, stable, and **drives the `data-testid`** that
 *    `shellNavItemTestId()` derives for the rendered nav node. Renaming a key
 *    silently breaks every E2E selector that targets it. `label` is the
 *    user-visible string and is free to change; test IDs never derive from it.
 * 2. A **group** node has **no `href`** — it only expands and collapses. Nodes
 *    with children are never themselves marked active; a node auto-expands when
 *    it or a direct child is active.
 * 3. Icons are `lucide-react` components, always `className="h-4 w-4"`, and are
 *    kept visually distinct between sibling groups.
 * 4. Every `href` comes from {@link ROUTES} — never a raw string literal, so a
 *    route rename is a compile error rather than a dead link.
 *
 * All helpers below are pure: they return new trees and never mutate their
 * input. A group that filters down to zero visible children is pruned — a group
 * that expands into nothing is worse than no group at all.
 *
 * Deliberately absent (routes/pages that do not exist, or duplicates):
 *   - EventOps rules — the orphan `EventOpsRulesListPage` file was deleted (no route constant,
 *     never mounted, dead code).
 *   - RPA systems — the orphan `RpaSystemsListPage` file was deleted (no route constant, never
 *     mounted, dead code); the shipped RPA surface is Web Automation (sites + runs) under
 *     Integrations.
 *   - `ROUTES.EXTERNAL_TASK_SYSTEMS` is now surfaced under HITL → External Task Systems: the
 *     route mounts `ExternalTaskSystemsListPage` (fixed regression — it used to mount
 *     `TaskListPage` from a copy-paste).
 */

import {
  Activity,
  AudioLines,
  BarChart3,
  Bell,
  BookMarked,
  BookOpen,
  Bot,
  Brain,
  Bug,
  CalendarClock,
  CheckCircle2,
  ClipboardList,
  Cloud,
  Code2,
  Cog,
  Cpu,
  Database,
  FileCode2,
  FileText,
  GitBranch,
  Globe,
  History,
  KeyRound,
  LayoutDashboard,
  LifeBuoy,
  Link2,
  List,
  ListChecks,
  Mail,
  MessageSquare,
  Mic,
  Play,
  Plug,
  Scale,
  Settings,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Terminal,
  TrendingUp,
  User,
  UserCheck,
  Waypoints,
  Workflow,
  Wrench,
} from 'lucide-react';
import type { ShellSidebarItem } from '@coordio/ui';

import type { EnabledModules } from '@/store/contextStore';
import { ROUTES } from '@/utils/routes';

/* -------------------------------------------------------------------------- */
/* Permission model                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Feature modules Cortex can switch off per deployment.
 *
 * Structurally identical to `ModuleKey` in `@/@menu/menuItems`; declared here so
 * the shell config does not depend on the legacy `@menu` tree, which is removed
 * once the shell swap lands. Keep the two in sync until then.
 */
export type CortexModuleKey = 'Automation' | 'Governance' | 'Integrations';

/**
 * What a viewer must satisfy before a nav node is shown.
 *
 * Cortex has no fine-grained resource permissions (there is no
 * `Permission.X.Read` the way CoordioCore has). Its real access primitives are
 * exactly three, and this type is their union:
 *   - `module`               the feature-module toggle enforced by `ModuleGuard`
 *                            (`utils/moduleRoutes.ts`, `store/contextStore.ts`)
 *   - `requiresTenant`       a tenant must be selected in the context store
 *                            (the legacy menu's `requiresTenant` flag)
 *   - `requiresGlobalAccess` the token carries a global role
 *                            (`utils/globalAccess.ts` -> `isGlobalAccess`)
 *
 * All fields are ANDed. A key absent from the map has no requirement and is
 * always visible.
 */
export interface PermissionRequirement {
  readonly module?: CortexModuleKey;
  readonly requiresTenant?: boolean;
  readonly requiresGlobalAccess?: boolean;
}

/** The viewer state a {@link PermissionRequirement} is evaluated against. */
export interface CortexSidebarViewer {
  /** True when `contextStore.currentTenant` is set. */
  readonly hasTenant: boolean;
  /** `contextStore.isGlobalAccess`. */
  readonly isGlobalAccess: boolean;
  /** `contextStore.enabledModules`. */
  readonly enabledModules: EnabledModules;
}

/** Predicate shape accepted by {@link filterSidebarItemsByPermissions}. */
export type CortexSidebarPermissionChecker = (
  requirement: PermissionRequirement,
) => boolean;

/**
 * Builds the `can` predicate for {@link filterSidebarItemsByPermissions} from a
 * context-store snapshot. Pure — the caller owns the snapshot.
 */
export function createCortexSidebarPermissionChecker(
  viewer: CortexSidebarViewer,
): CortexSidebarPermissionChecker {
  return (requirement: PermissionRequirement): boolean => {
    if (requirement.requiresTenant && !viewer.hasTenant) return false;
    if (requirement.requiresGlobalAccess && !viewer.isGlobalAccess) return false;
    if (!requirement.module) return true;
    switch (requirement.module) {
      case 'Automation':
        return viewer.enabledModules.automation;
      case 'Governance':
        return viewer.enabledModules.governance;
      case 'Integrations':
        return viewer.enabledModules.integrations;
      default:
        return true;
    }
  };
}

/* -------------------------------------------------------------------------- */
/* The tree                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The Cortex sidebar. Groups (no `href`) collapse; leaves link through
 * {@link ROUTES}. Nesting goes at most three levels deep, as in CoordioCore.
 */
export const cortexSidebarItems: readonly ShellSidebarItem[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    href: ROUTES.HOME,
    icon: <LayoutDashboard className="h-4 w-4" />,
  },
  {
    // WHY: reporting surfaces sit together so an operator comparing spend,
    // latency and widget layout never leaves the group.
    key: 'insights',
    label: 'Insights',
    icon: <BarChart3 className="h-4 w-4" />,
    children: [
      {
        key: 'dashboard-customize',
        label: 'Customize',
        href: ROUTES.DASHBOARD_CUSTOMIZE,
        icon: <SlidersHorizontal className="h-4 w-4" />,
      },
      {
        key: 'usage-analytics',
        label: 'Usage Analytics',
        href: ROUTES.USAGE_ANALYTICS,
        icon: <TrendingUp className="h-4 w-4" />,
      },
      {
        key: 'llm-metrics',
        label: 'LLM Metrics',
        href: ROUTES.LLM_METRICS,
        icon: <Activity className="h-4 w-4" />,
      },
    ],
  },
  {
    // WHY a leaf, not a group: `ROUTES.PROJECT_EDIT` is a detail route reached
    // from the list, so there is no second navigable project page to group.
    key: 'projects',
    label: 'Projects',
    href: ROUTES.PROJECTS,
    icon: <Workflow className="h-4 w-4" />,
  },
  {
    // WHY: the agent runtime domain — everything that defines or observes an
    // agent execution. Memory is large enough to be its own root group below.
    key: 'ai-operations',
    label: 'AI Operations',
    icon: <Sparkles className="h-4 w-4" />,
    children: [
      {
        key: 'agent-list',
        label: 'Agents',
        href: ROUTES.AGENTS,
        icon: <Bot className="h-4 w-4" />,
      },
      {
        key: 'session-list',
        label: 'Sessions',
        href: ROUTES.SESSIONS,
        icon: <MessageSquare className="h-4 w-4" />,
      },
      {
        key: 'tool-list',
        label: 'Tools',
        href: ROUTES.TOOLS,
        icon: <Wrench className="h-4 w-4" />,
      },
      {
        // Third level: the function catalog and the ad-hoc execute console are
        // two distinct pages over the same resource.
        key: 'agent-function',
        label: 'Agent Functions',
        icon: <Code2 className="h-4 w-4" />,
        children: [
          {
            key: 'agent-function-list',
            label: 'List',
            href: ROUTES.AGENT_FUNCTIONS,
            icon: <List className="h-4 w-4" />,
          },
          {
            key: 'agent-function-execute',
            label: 'Execute',
            href: ROUTES.AGENT_FUNCTION_EXECUTE,
            icon: <Play className="h-4 w-4" />,
          },
        ],
      },
      {
        key: 'skill-list',
        label: 'Skills',
        href: ROUTES.SKILLS,
        icon: <ClipboardList className="h-4 w-4" />,
      },
      {
        key: 'model-catalog',
        label: 'Model Catalog',
        href: ROUTES.MODEL_CATALOG,
        icon: <Brain className="h-4 w-4" />,
      },
      {
        // Suppressed by default — see CORTEX_SIDEBAR_SUPPRESSED_KEYS.
        key: 'orchestrator',
        label: 'Orchestrator',
        href: ROUTES.ORCHESTRATOR,
        icon: <Waypoints className="h-4 w-4" />,
      },
    ],
  },
  {
    // WHY memory is a root group and not an AI Operations child: six pages
    // (records, definitions, policy, rules, pipeline, quarantine) would
    // dominate that group and bury the agent pages.
    key: 'memory',
    label: 'Memory',
    icon: <Database className="h-4 w-4" />,
    children: [
      {
        key: 'memory-list',
        label: 'Records',
        href: ROUTES.MEMORIES,
        icon: <List className="h-4 w-4" />,
      },
      {
        key: 'memory-definition-list',
        label: 'Definitions',
        href: ROUTES.MEMORY_DEFINITIONS,
        icon: <FileCode2 className="h-4 w-4" />,
      },
      {
        key: 'memory-policy',
        label: 'Policy',
        href: ROUTES.MEMORY_POLICY,
        icon: <Scale className="h-4 w-4" />,
      },
      {
        key: 'memory-rule-list',
        label: 'Rules',
        href: ROUTES.MEMORY_RULES,
        icon: <BookMarked className="h-4 w-4" />,
      },
      {
        key: 'memory-pipeline',
        label: 'Pipeline',
        href: ROUTES.MEMORY_PIPELINE,
        icon: <GitBranch className="h-4 w-4" />,
      },
      {
        key: 'memory-quarantine',
        label: 'Quarantine',
        href: ROUTES.MEMORY_QUARANTINE,
        icon: <ShieldAlert className="h-4 w-4" />,
      },
    ],
  },
  {
    key: 'connectors',
    label: 'Connectors',
    href: ROUTES.CONNECTORS,
    icon: <Link2 className="h-4 w-4" />,
  },
  {
    key: 'integrations',
    label: 'Integrations',
    icon: <Plug className="h-4 w-4" />,
    children: [
      {
        key: 'mcp-server-list',
        label: 'MCP Servers',
        href: ROUTES.MCP_SERVERS,
        icon: <Cloud className="h-4 w-4" />,
      },
      {
        key: 'external-system-list',
        label: 'External Systems',
        href: ROUTES.EXTERNAL_SYSTEMS,
        icon: <Globe className="h-4 w-4" />,
      },
      {
        // WHY this is the RPA entry: the shipped RPA surface is the web
        // automation site catalog plus its run history. The previous
        // `RpaSystemsListPage` was an orphan with no route constant and has
        // been deleted, so it is not linked.
        key: 'web-automation',
        label: 'RPA (Web Automation)',
        icon: <Workflow className="h-4 w-4" />,
        children: [
          {
            key: 'web-automation-site-list',
            label: 'Sites',
            href: ROUTES.WEB_AUTOMATION_SITES,
            icon: <List className="h-4 w-4" />,
          },
          {
            key: 'web-automation-run-list',
            label: 'Runs',
            href: ROUTES.WEB_AUTOMATION_RUNS,
            icon: <History className="h-4 w-4" />,
          },
        ],
      },
    ],
  },
  {
    // WHY: human-in-the-loop work — the queue a person must act on, whether it
    // arrived as a task or as an approval an agent is blocked on.
    key: 'hitl',
    label: 'Human in the Loop',
    icon: <UserCheck className="h-4 w-4" />,
    children: [
      {
        key: 'task-list',
        label: 'Tasks',
        href: ROUTES.TASKS,
        icon: <ListChecks className="h-4 w-4" />,
      },
      {
        key: 'approval-queue',
        label: 'Approval Queue',
        href: ROUTES.APPROVAL_QUEUE,
        icon: <CheckCircle2 className="h-4 w-4" />,
      },
    ],
  },
  {
    key: 'investigations',
    label: 'Investigations',
    icon: <Bug className="h-4 w-4" />,
    children: [
      {
        key: 'investigation-list',
        label: 'List',
        href: ROUTES.INVESTIGATIONS,
        icon: <List className="h-4 w-4" />,
      },
      {
        key: 'investigation-template-list',
        label: 'Templates',
        href: ROUTES.INVESTIGATION_TEMPLATES,
        icon: <FileText className="h-4 w-4" />,
      },
    ],
  },
  {
    key: 'workflows',
    label: 'Workflows',
    icon: <Waypoints className="h-4 w-4" />,
    children: [
      {
        key: 'workflow-list',
        label: 'List',
        href: ROUTES.WORKFLOWS,
        icon: <List className="h-4 w-4" />,
      },
      {
        key: 'composer',
        label: 'Agent Composer',
        href: ROUTES.COMPOSER,
        icon: <Sparkles className="h-4 w-4" />,
      },
    ],
  },
  {
    key: 'schedules',
    label: 'Schedules',
    href: ROUTES.SCHEDULES,
    icon: <CalendarClock className="h-4 w-4" />,
  },
  {
    key: 'safety-gates',
    label: 'Safety Gates',
    href: ROUTES.SAFETY_GATES,
    icon: <ShieldCheck className="h-4 w-4" />,
  },
  {
    key: 'guardrails',
    label: 'Guardrails',
    href: ROUTES.GUARDRAILS,
    icon: <ShieldAlert className="h-4 w-4" />,
  },
  {
    key: 'transcription',
    label: 'Transcriptions',
    icon: <AudioLines className="h-4 w-4" />,
    children: [
      {
        key: 'transcription-list',
        label: 'Transactions',
        href: ROUTES.TRANSCRIPTIONS,
        icon: <List className="h-4 w-4" />,
      },
      {
        key: 'transcription-playground',
        label: 'Playground',
        href: ROUTES.TRANSCRIPTION_PLAYGROUND,
        icon: <Mic className="h-4 w-4" />,
      },
      {
        key: 'transcription-how-to-use',
        label: 'How to Use',
        href: ROUTES.TRANSCRIPTION_HOW_TO_USE,
        icon: <BookOpen className="h-4 w-4" />,
      },
    ],
  },
  {
    key: 'api-explorer',
    label: 'API Explorer',
    href: ROUTES.API_EXPLORER,
    icon: <Terminal className="h-4 w-4" />,
  },
  {
    key: 'developer-help',
    label: 'Developer Help',
    href: ROUTES.DEVELOPER_HELP,
    icon: <LifeBuoy className="h-4 w-4" />,
  },
  {
    key: 'settings',
    label: 'Settings',
    icon: <Settings className="h-4 w-4" />,
    children: [
      {
        key: 'settings-tenant',
        label: 'Tenant Settings',
        href: ROUTES.SETTINGS_TENANT,
        icon: <Cog className="h-4 w-4" />,
      },
      {
        key: 'settings-user',
        label: 'User Settings',
        href: ROUTES.SETTINGS_USER,
        icon: <User className="h-4 w-4" />,
      },
      {
        key: 'api-key-list',
        label: 'API Keys',
        href: ROUTES.API_KEYS,
        icon: <KeyRound className="h-4 w-4" />,
      },
      {
        key: 'llm-provider-config-list',
        label: 'LLM Providers',
        href: ROUTES.LLM_PROVIDER_CONFIGS,
        icon: <Cpu className="h-4 w-4" />,
      },
      {
        key: 'email-template-list',
        label: 'Email Templates',
        href: ROUTES.EMAIL_TEMPLATES,
        icon: <Mail className="h-4 w-4" />,
      },
      {
        key: 'notification-log',
        label: 'Notification Log',
        href: ROUTES.NOTIFICATIONS,
        icon: <Bell className="h-4 w-4" />,
      },
    ],
  },
];

/* -------------------------------------------------------------------------- */
/* Permission map                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Requirement per sidebar `key`. Keys absent from this map carry no requirement
 * and are always visible. Groups are mapped alongside their children so a whole
 * subtree drops in one step and stays correct if a child is re-parented.
 */
export const CORTEX_SIDEBAR_PERMISSION_MAP: Partial<
  Record<string, PermissionRequirement>
> = {
  // All menu items are always visible so the full navigation is available
  // regardless of tenant selection. Individual pages handle missing-tenant
  // gracefully with empty states. Only truly restricted items are gated:

  // Memory quarantine — admin-only
  'memory-quarantine': { requiresGlobalAccess: true },

  // Tenant roster — cross-tenant admin
  'tenant-list': { requiresGlobalAccess: true },

  // LLM provider config — admin
  'llm-provider-config-list': { requiresGlobalAccess: true },
};

/**
 * Escape hatch for nav nodes whose route exists but that must not be shown yet.
 *
 * `orchestrator` is listed because the legacy `@menu` entry was commented out
 * while the page is incomplete; the route is still mounted, so the node stays in
 * the tree and deleting one line here brings it back.
 */
export const CORTEX_SIDEBAR_SUPPRESSED_KEYS: ReadonlySet<string> = new Set([
  'investigations',
]);

/* -------------------------------------------------------------------------- */
/* Active state                                                               */
/* -------------------------------------------------------------------------- */

/** Strips query/hash and any trailing slash so `/projects/` === `/projects`. */
function normalizePathname(value: string | undefined): string {
  const raw = (value ?? '').trim();
  if (!raw) return '';
  const path = raw.split('?')[0].split('#')[0];
  if (path.length > 1 && path.endsWith('/')) return path.replace(/\/+$/, '');
  return path;
}

function collectHrefs(
  items: readonly ShellSidebarItem[],
  into: Set<string> = new Set<string>(),
): Set<string> {
  for (const item of items) {
    const href = normalizePathname(item.href);
    if (href) into.add(href);
    if (item.children?.length) collectHrefs(item.children, into);
  }
  return into;
}

/**
 * Every configured `href`, normalized. Used to decide which of two overlapping
 * routes owns a pathname (e.g. `/investigations/templates` must light up
 * Templates, not the Investigations list whose path it nests under).
 */
const CORTEX_SIDEBAR_HREFS: ReadonlySet<string> = collectHrefs(cortexSidebarItems);

function hasMoreSpecificHref(pathname: string, href: string): boolean {
  for (const candidate of CORTEX_SIDEBAR_HREFS) {
    if (candidate === href || candidate.length <= href.length) continue;
    if (pathname === candidate || pathname.startsWith(`${candidate}/`)) return true;
  }
  return false;
}

/**
 * Whether a nav node is the one the current pathname belongs to.
 *
 * Mirrors CoordioCore's `isShellSidebarItemActive`:
 *   - a group (a node with children) is never active — it only expands;
 *   - a leaf is active on an exact match, or when the pathname is a child route
 *     of its `href` (`/projects/123` activates `projects`);
 *   - the root `href` (`/`) matches only exactly, otherwise every route in the
 *     app would activate Dashboard;
 *   - a prefix match loses to a longer configured `href` that also matches, so
 *     two nav nodes never light up for one pathname.
 */
export function isCortexSidebarItemActive(
  item: Pick<ShellSidebarItem, 'href' | 'children'>,
  pathname: string,
): boolean {
  if (item.children?.length) return false;

  const href = normalizePathname(item.href);
  const path = normalizePathname(pathname);
  if (!href || !path) return false;
  if (href === path) return true;
  if (href === '/') return false;
  if (!path.startsWith(`${href}/`)) return false;

  return !hasMoreSpecificHref(path, href);
}

/**
 * Returns a new tree with `isActive` set from `pathname`. Never mutates input.
 */
export function markActive(
  items: readonly ShellSidebarItem[],
  pathname: string,
): ShellSidebarItem[] {
  return items.map((item) => ({
    ...item,
    isActive: isCortexSidebarItemActive(item, pathname),
    children: item.children?.length ? markActive(item.children, pathname) : undefined,
  }));
}

/* -------------------------------------------------------------------------- */
/* Filtering                                                                  */
/* -------------------------------------------------------------------------- */

function normalizeKey(key: string | undefined): string {
  return String(key ?? '').trim().toLowerCase();
}

/**
 * Drops nav nodes the viewer may not see, keyed by
 * {@link CORTEX_SIDEBAR_PERMISSION_MAP}. A node whose own requirement fails
 * takes its whole subtree with it; a group left with zero visible children is
 * pruned, because a group that expands into nothing is worse than no group.
 * Pure — returns a new tree.
 */
export function filterSidebarItemsByPermissions(
  items: readonly ShellSidebarItem[],
  can: CortexSidebarPermissionChecker,
): ShellSidebarItem[] {
  return items.flatMap((item): ShellSidebarItem[] => {
    const requirement = CORTEX_SIDEBAR_PERMISSION_MAP[normalizeKey(item.key)];
    if (requirement && !can(requirement)) return [];

    if (item.children?.length) {
      const children = filterSidebarItemsByPermissions(item.children, can);
      return children.length > 0 ? [{ ...item, children }] : [];
    }

    return [{ ...item, children: undefined }];
  });
}

/**
 * Removes suppressed keys from the tree (root and nested `children`), pruning
 * any group left with zero visible children. Pure — returns a new tree.
 */
export function omitSuppressedSidebarKeys(
  items: readonly ShellSidebarItem[],
  suppressed: ReadonlySet<string> = CORTEX_SIDEBAR_SUPPRESSED_KEYS,
): ShellSidebarItem[] {
  return items.flatMap((item): ShellSidebarItem[] => {
    if (suppressed.has(normalizeKey(item.key))) return [];

    if (item.children?.length) {
      const children = omitSuppressedSidebarKeys(item.children, suppressed);
      return children.length > 0 ? [{ ...item, children }] : [];
    }

    return [{ ...item, children: undefined }];
  });
}

/**
 * The composition the shell uses: suppress, then permission-filter, then mark
 * the active node. Pure; the caller owns the viewer snapshot and the pathname.
 */
export function resolveCortexSidebarItems(
  pathname: string,
  can: CortexSidebarPermissionChecker,
  items: readonly ShellSidebarItem[] = cortexSidebarItems,
): ShellSidebarItem[] {
  return markActive(
    filterSidebarItemsByPermissions(omitSuppressedSidebarKeys(items), can),
    pathname,
  );
}
